/**
 * Every server call the NEW pages make, grouped by area. (Windows reused from today's site still make their own calls
 * - see PLAN.md §2 - and go through the same interceptor / demo server.)
 * Paths are Caryn's live API (pd-backend); shapes were read from cc/backend-insights controllers on 2026-11-03.
 */
import { api } from './client'
import { WELCOME_MAIL, DIARY_FALLBACK, type DiaryPost } from '~/content/content'
export type { DiaryPost }

// ---------------------------------------------------------------- types
export interface Creator { id: string; username: string | null; avatar_url: string | null }
export interface Conversation { id: string; creator_id: string; fan_id: string; unread_count: number; last_message_at: string; last_message_content: string | null; creator?: Creator }
export interface ChatMessage { id: string; sender_id: string; recipient_id: string; content: string; created_at: string; read_at: string | null; attachments?: any[] }
export interface GalleryImage {
  id: string; album_id: string | null; title: string | null; original_width: number; original_height: number;
  access_level: 'public' | 'subscribers' | 'premium'; hasAccess: boolean; price: number | null; dominant_color: string | null;
  urls: { placeholder: string | null; thumbnail: string | null; medium: string | null; full: string | null; original: string | null }
}
export interface Album { id: string; name: string; slug: string; image_count?: number }
export interface Mail {
  id: string; from: string; subject: string; preview: string; at: string; body: string[]; links: [string, string][]; photo?: string | null; starred?: boolean; read?: boolean
  /** tracking (server): first open, number of opens, "Mark as read" time; deletesAt = first open + deleteHoursAfterOpen */
  openedAt?: string | null; openCount?: number; readAt?: string | null; deletesAt?: string | null; deleteHoursAfterOpen?: number | null
  /** false = the server has no mail routes yet; state is kept on this device */
  tracked?: boolean
}
export interface PaymentCard { id: string; brand: string; last4: string; exp_month: number; exp_year: number; is_default: boolean }
export interface VaultItem { id: string; kind: 'purchased' | 'saved'; src: string | null; mediaType: 'image' | 'video' | 'audio' | string; title: string; at: string; galleryImage?: GalleryImage }

// ---------------------------------------------------------------- session
export const auth = {
  signOut: () => api('/api/auth/signout', { method: 'POST', body: {} }),
  presenceLeave: () => api('/api/messages/presence/leave', { method: 'POST', body: {} }),
}

// ---------------------------------------------------------------- texts (chat)
export const texts = {
  conversation: async () => {
    const r = await api<{ data: Conversation[] }>('/api/messages/user/conversations')
    return r.data?.data?.[0] ?? null
  },
  /** Newest first from the server; returned oldest first. */
  recent: async (conversationId: string, limit = 50) => {
    const r = await api<{ data: ChatMessage[] }>(`/api/messages/${conversationId}?limit=${limit}`)
    return (r.data?.data ?? []).slice().reverse()
  },
}

// ---------------------------------------------------------------- vip
export const vip = {
  isVip: async () => (await api<{ status?: string }>('/api/vip/progress')).data?.status === 'vip',
}

// ---------------------------------------------------------------- gallery
export const gallery = {
  albums: async () => (await api<{ data: Album[] }>('/api/gallery/albums')).data?.data ?? [],
  images: async (limit = 60, offset = 0) => {
    const r = await api<{ data: GalleryImage[]; pagination?: { total?: number } }>(`/api/gallery/images?limit=${limit}&offset=${offset}`)
    return { images: r.data?.data ?? [], total: r.data?.pagination?.total ?? null, ok: r.ok }
  },
  album: async (slug: string) => (await api<{ images: GalleryImage[] }>(`/api/gallery/albums/${encodeURIComponent(slug)}`)).data?.images ?? [],
  likedIds: async () => new Set((await api<{ data: string[] }>('/api/gallery/likes/ids')).data?.data ?? []),
  purchasedIds: async () => new Set((await api<{ data: string[] }>('/api/gallery/purchases/ids')).data?.data ?? []),
  like: async (id: string) => (await api<{ liked: boolean }>(`/api/gallery/images/${id}/like`, { method: 'POST', body: {} })).data?.liked ?? null,
  /** Full-size signed URL for one image (only works when the fan has access). */
  url: async (id: string, resolution: 'medium' | 'full' = 'full') => (await api<{ url: string }>(`/api/gallery/images/${id}/url?resolution=${resolution}`)).data?.url ?? null,
  sessionStart: () => api('/api/vip/gallery-session/start', { method: 'POST', body: {} }),
  sessionEnd: () => api('/api/vip/gallery-session/end', { method: 'POST', body: {} }),
}

// ---------------------------------------------------------------- vault (purchased + saved)
export const vault = {
  items: async (): Promise<VaultItem[]> => {
    const [p, f, liked] = await Promise.all([
      api<{ data: any[] }>('/api/purchases'),
      api<{ data: any[] }>('/api/freebie-unlocks'),
      api<{ data: any[] }>('/api/gallery/likes'),
    ])
    const out: VaultItem[] = []
    for (const x of p.data?.data ?? []) {
      const it = x.photo || {}
      out.push({ id: `p-${x.id}`, kind: 'purchased', src: it.full_url || it.url || it.preview_url || null, mediaType: it.media_type || 'image', title: it.title || 'Purchase', at: x.purchased_at || x.created_at })
    }
    for (const x of f.data?.data ?? []) {
      const it = x.freebie || {}
      out.push({ id: `f-${x.id}`, kind: 'purchased', src: it.full_url || it.url || it.preview_url || null, mediaType: it.media_type || 'image', title: it.title || 'Gift', at: x.unlocked_at || x.created_at })
    }
    for (const x of liked.data?.data ?? []) {
      const img: GalleryImage | undefined = x.image || x.gallery_image || (x.urls ? x : undefined)
      if (!img) continue
      out.push({ id: `s-${img.id}`, kind: 'saved', src: img.urls?.medium || img.urls?.thumbnail || null, mediaType: 'image', title: img.title || 'Saved photo', at: x.liked_at || x.created_at || '', galleryImage: img })
    }
    return out.sort((a, b) => (b.at || '').localeCompare(a.at || ''))
  },
}

// ---------------------------------------------------------------- email (inbox) + diary
/**
 * Caryn's server (messaging-service, fanMailController.js): the fan's mail (for now one Welcome email) with tracking -
 * opening is recorded (first/last open, count), "Mark as read" is recorded, and a mail deletes 24 h after its first open.
 * If the server has not been updated yet, the same Welcome email shows with its open/read state on this device only.
 */
const LOCAL_KEY = 'cw-mail-local'
const localState = (): Record<string, Partial<Mail>> => { try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}') } catch { return {} } }
const saveLocal = (id: string, p: Partial<Mail>) => { try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ ...localState(), [id]: { ...localState()[id], ...p } })) } catch { /* private mode */ } }
const toMail = (m: any): Mail => ({
  id: m.id, from: m.from || 'Caryn', subject: m.subject, preview: m.preview || '', at: m.at || new Date().toISOString(), body: m.body || [],
  links: m.links || [], openedAt: m.openedAt || null, openCount: m.openCount || 0, readAt: m.readAt || null, read: !!m.readAt,
  deletesAt: m.deletesAt || null, deleteHoursAfterOpen: m.deleteHoursAfterOpen ?? null, tracked: m.tracked !== false,
})
const fallbackMail = (): Mail => {
  const st = localState()[WELCOME_MAIL.id] || {}
  return toMail({ ...WELCOME_MAIL, at: WELCOME_MAIL.sentAt, ...st, deletesAt: null, tracked: false })
}
export const inbox = {
  list: async (): Promise<Mail[]> => {
    const r = await api<{ data: any[] }>('/api/messages/fan-mail')
    if (r.ok) return (r.data?.data ?? []).map(toMail)
    // the server has no mail routes yet (today's /api/messages/:conversationId answers 404) or failed: same Welcome email
    return [fallbackMail()]
  },
  /** record an open (tracked on the server); returns the mail's new state, or null if it was deleted */
  open: async (m: Mail): Promise<Mail | null> => {
    if (!m.tracked) { const p = { openedAt: m.openedAt || new Date().toISOString(), openCount: (m.openCount || 0) + 1 }; saveLocal(m.id, p); return { ...m, ...p } }
    const r = await api<{ data: any }>(`/api/messages/fan-mail/${encodeURIComponent(m.id)}/open`, { method: 'POST' })
    if (r.status === 410) return null
    return r.ok && r.data?.data ? toMail(r.data.data) : m
  },
  markRead: async (m: Mail, read: boolean): Promise<Mail> => {
    if (!m.tracked) { const p = { readAt: read ? new Date().toISOString() : null }; saveLocal(m.id, p); return { ...m, ...p, read } }
    const r = await api<{ data: any }>(`/api/messages/fan-mail/${encodeURIComponent(m.id)}/read`, { method: 'POST', body: { read } })
    if (!r.ok) throw new Error(r.error || 'Could not save that')
    return toMail(r.data!.data)
  },
}

export const diary = {
  list: async (): Promise<DiaryPost[]> => {
    const r = await api<{ data: any[] }>('/api/messages/diary')
    if (!r.ok) return DIARY_FALLBACK
    return (r.data?.data ?? []).map((p): DiaryPost => ({
      date: /^\d{4}-\d{2}-\d{2}$/.test(p.date || '') ? new Date(p.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (p.date || ''),
      mood: p.mood || '', title: p.title || '', text: p.text || [], photo: p.photo || undefined, alt: p.alt, gallery: !!p.gallery,
    }))
  },
}

// ---------------------------------------------------------------- PIN (saved on the account)
/**
 * Caryn's server (messaging-service pinController.js): the PIN is saved on the fan's account (scrypt hash), so it works
 * in every browser and phone. null = this server has no PIN routes yet (the PIN then stays on this device, as before).
 */
export const pinApi = {
  status: async (): Promise<boolean | null> => { const r = await api<{ hasPin: boolean }>('/api/messages/pin'); return r.ok ? !!r.data?.hasPin : null },
  set: async (pin: string, current?: string): Promise<boolean> => (await api('/api/messages/pin', { method: 'POST', body: { pin, current } })).ok,
  verify: async (pin: string): Promise<{ ok: boolean; triesLeft?: number; locked?: boolean } | null> => {
    const r = await api<{ ok: boolean; triesLeft?: number }>('/api/messages/pin/verify', { method: 'POST', body: { pin } })
    if (r.status === 429) return { ok: false, locked: true }
    return r.ok ? (r.data || { ok: false }) : null
  },
  reset: async (): Promise<boolean> => (await api('/api/messages/pin/reset', { method: 'POST' })).ok,
}

// ---------------------------------------------------------------- settings
export const settings = {
  preferences: async (userId: string) => {
    const d = (await api<any>(`/api/customer/preferences/${userId}`)).data || {}
    return { email: !!(d.notifications_email ?? d.email_notifications ?? d.preferences?.notifications_email), sms: !!(d.notifications_sms ?? d.sms_notifications ?? d.preferences?.notifications_sms) }
  },
  setPreference: (userId: string, which: 'email' | 'sms', on: boolean) =>
    api('/api/customer/preferences', { method: 'PATCH', body: { userId, [which === 'email' ? 'notifications_email' : 'notifications_sms']: on } }),
  walletBalance: async () => { const d = (await api<any>('/api/wallet/balance')).data; return typeof d?.balance === 'number' ? d.balance : null },
  cards: async (): Promise<PaymentCard[]> => (await api<any>('/api/payment-methods')).data?.payment_methods ?? [],
  subscription: async () => (await api<any>('/api/subscriptions/current')).data?.subscription ?? null,
}
